/**
 * Risk-tier color/label tokens for inline citations.
 *
 * Reuses the canonical client-portal palette (`getActionColor()` /
 * `impactColorMap` in `vision/apps/client-portal/src/utils/post.ts`) and the
 * client-portal i18n risk labels. Tier numbering follows the client-portal
 * impact scale: 1 = low … 4 = very high; `null` = N/A. "Highest severity" is
 * the largest tier number.
 *
 * Do NOT redefine these hex values elsewhere — they are the shared tokens.
 */

/** Full-strength dot/accent colors (client-portal `impactColorMap`). */
export const RISK_COLORS: Record<string, string> = {
  '1': '#369d66', // low
  '2': '#f4b868', // medium
  '3': '#ed7c4b', // high
  '4': '#a43d4a', // very high
}

/** N/A grey (client-portal `DEFAULT_COLOR`). */
export const RISK_NA_COLOR = '#a6a6a6'

/**
 * Darkened text variants for amber/grey so labels meet WCAG AA on white.
 * Dots stay at full color (decorative + paired with text).
 */
export const RISK_TEXT_COLORS: Record<string, string> = {
  '1': '#2c7d51', // low (green, darkened)
  '2': '#8a5a12', // medium (amber, darkened for AA)
  '3': '#c1542a', // high (orange, darkened)
  '4': '#a43d4a', // very high (red already AA on white)
}

export const RISK_NA_TEXT_COLOR = '#5f5f5f'

/** Client-portal i18n risk labels (`risk.risk_*_title`). */
export const RISK_LABELS: Record<string, string> = {
  '1': 'Low risk',
  '2': 'Medium risk',
  '3': 'High risk',
  '4': 'Very high risk',
}

export const RISK_NA_LABEL = 'N/A'

/** Resolve the dot/accent color for a tier (null/unknown → N/A grey). */
export function getRiskColor(tier: number | null | undefined): string {
  if (tier == null) return RISK_NA_COLOR
  return RISK_COLORS[String(tier)] ?? RISK_NA_COLOR
}

/** Resolve the AA-safe text color for a tier (null/unknown → N/A grey text). */
export function getRiskTextColor(tier: number | null | undefined): string {
  if (tier == null) return RISK_NA_TEXT_COLOR
  return RISK_TEXT_COLORS[String(tier)] ?? RISK_NA_TEXT_COLOR
}

/** Resolve the human-readable label for a tier (null/unknown → "N/A"). */
export function getRiskLabel(tier: number | null | undefined): string {
  if (tier == null) return RISK_NA_LABEL
  return RISK_LABELS[String(tier)] ?? RISK_NA_LABEL
}

/**
 * Parse an Azure `impact` string (e.g. "2 (medium)") into a numeric tier.
 * Returns null for missing/unrecognized values (rendered as N/A).
 */
export function parseImpactToTier(impact: unknown): number | null {
  if (impact == null) return null
  const s = String(impact).trim()
  if (!s) return null
  const first = s[0]
  if (first === '1' || first === '2' || first === '3' || first === '4') {
    return Number(first)
  }
  return null
}
